import { reactive, toRefs, watch } from 'vue';
import difference from 'lodash/difference';
import debounce from 'lodash/debounce';
import Validator from '@/seatplan/Validator';
import Group from '@/seatplan/Group';
import clone from '@/helpers/clone';
import { Inertia } from '@inertiajs/inertia';
import echo from '@/helpers/echo';
import events from '@/helpers/events';

export default function useSeatplan(url, rules, watcher) {

  const { on, once, emit, off } = events();

  const state = reactive({
    accepted: [],
    selected: [],
    seats: {},
  });

  const previous = {
    selected: [],
    accepted: [],
    hash: null,
  }

  const validator = new Validator(rules);

  function setup(seatplan) {
    state.seats = {};

    seatplan.groups.forEach(group => {
      new Group(group, state, validator, watcher);
    });
  }

  let stop;

  function start() {

    // Make sure that any currently running watcher is stopped so we do not end
    // up with multiple watches creating all kinds of problems
    stop && stop();

    stop = watch(() => state.selected, debounce(() => {
      const selected = clone(state.selected);
      const accepted = clone(state.accepted);

      selected.sort();

      const hash = JSON.stringify(selected);

      // Prevent unnecessary traffic
      if (hash === previous.hash)
        return;

      previous.hash = hash;

      Inertia.post(url, { selected }, {
        preserveScroll: true,
        headers: {
          'X-Socket-ID': echo.socketId(),
        },
        onSuccess() {
          emit('success', [selected, accepted]);
          state.accepted = selected;
        },
        onError() {
          emit('error', [selected, accepted]);
          stop();
          state.selected = accepted;
          previous.hash = null;
          start();
        },
      });

    }, 500, { maxWait: 2000 }), { flush: 'sync', deep: true });
  }

  function diff(a, b, callback) {
    difference(a, b).forEach(key => {
      if (key in state.seats) callback(state.seats[key]);
    });
  }

  watch(() => state.selected, selected => {
    const value = clone(selected);

    diff(previous.selected, value, seat => {
      seat.state.selected = false;
      seat.flush();
    });

    diff(value, previous.selected, seat => {
      seat.state.selected = true;
      seat.flush();
    });

    previous.selected = value;
  }, { deep: true });

  watch(() => state.accepted, accepted => {
    const value = clone(accepted);

    diff(previous.accepted, value, seat => {
      seat.state.accepted = null;
    });

    diff(value, previous.accepted, seat => {
      seat.state.accepted = true;
    });

    previous.accepted = value;
  }, { deep: true });

  return {
    ...toRefs(state),
    setup,
    start,
    on,
    once,
    off,
  }
}
